import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart2, Clock, Target, BarChart3, Activity, PieChart, Timer, Building2, Users, Copy, CheckCircle, Calculator } from 'lucide-react';
import { CalculatedMetrics } from '../types';
import { formatVolume, formatPercentage, formatMarketCap, calculateAggregatedTickerMetrics, AggregatedTickerMetrics } from '../utils/calculations';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface TickerDetailProps {
  data: CalculatedMetrics[];
}

export const TickerDetail: React.FC<TickerDetailProps> = ({ data }) => {
  const { tickerSymbol } = useParams<{ tickerSymbol: string }>();
  const navigate = useNavigate();
  const [copySuccess, setCopySuccess] = React.useState(false);

  // Filtra tutte le occorrenze di questo ticker
  const tickerOccurrences = data.filter(item => item.Ticker === tickerSymbol);

  if (tickerOccurrences.length === 0) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-400 hover:text-white mb-6 sm:mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Torna alla Dashboard</span>
        </button>
        
        <div className="text-center py-8 sm:py-12">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-4">Ticker Non Trovato</h1>
          <p className="text-sm sm:text-base text-gray-400">Il ticker "{tickerSymbol}" non è stato trovato nel dataset corrente.</p>
        </div>
      </div>
    );
  }

  // Calcola metriche aggregate per questo ticker
  const aggregatedMetrics: AggregatedTickerMetrics = calculateAggregatedTickerMetrics(tickerOccurrences);

  // Genera il testo del riepilogo
  const generateRecapText = () => {
    return `RIEPILOGO ANALISI TICKER: ${aggregatedMetrics.ticker}

Gap% medio: ${formatPercentage(aggregatedMetrics.averageGapPercent)}
Spike % medio: ${formatPercentage(aggregatedMetrics.averageSpikePercent)} (Mediana: ${formatPercentage(aggregatedMetrics.medianSpikePercent)})
Chiusura Rossa %: ${aggregatedMetrics.redClosePercentage.toFixed(1)}%
Break PMH: ${aggregatedMetrics.pmhBreakPercentage.toFixed(1)}%
Orario HOD medio: ${aggregatedMetrics.hodAverageTime}
Orario LOD medio: ${aggregatedMetrics.lodAverageTime}
Variazione % Media: ${formatPercentage(aggregatedMetrics.averageChangePercent)}
Ratio Volume Medio: ${aggregatedMetrics.averageVolumeRatio.toFixed(1)}x

Occorrenze totali: ${aggregatedMetrics.totalOccurrences}
Market Cap medio: ${formatMarketCap(aggregatedMetrics.averageMarketCap)}
Azioni in circolazione medie: ${formatVolume(aggregatedMetrics.averageSharesOutstanding)}`;
  };

  // Funzione per copiare il riepilogo
  const handleCopyRecap = async () => {
    try {
      await navigator.clipboard.writeText(generateRecapText());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Errore nella copia del riepilogo:', error);
    }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Torna alla Dashboard</span>
          </button>
          <div className="hidden sm:block h-6 w-px bg-gray-600"></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">{aggregatedMetrics.ticker}</h1>
            <p className="text-xs sm:text-sm text-gray-400">{aggregatedMetrics.totalOccurrences} occorrenze di gap analizzate</p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <button
            onClick={() => navigate('/backtesting')}
            className="flex items-center space-x-1 sm:space-x-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors text-xs sm:text-sm"
          >
            <Calculator className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Backtester</span>
          </button>
          <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
            aggregatedMetrics.averageGapPercent > 0
              ? 'bg-green-400/10 text-green-400'
              : 'bg-red-400/10 text-red-400'
          }`}>
            Gap Medio: {formatPercentage(aggregatedMetrics.averageGapPercent)}
          </div>
          <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
            aggregatedMetrics.averageChangePercent > 0
              ? 'bg-green-400/10 text-green-400'
              : 'bg-red-400/10 text-red-400'
          }`}>
            Variazione Media: {formatPercentage(aggregatedMetrics.averageChangePercent)}
          </div>
        </div>
      </div>

      {/* Box Riepilogo Copiabile */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
              <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-white">Riepilogo Statistiche</h3>
              <p className="text-xs sm:text-sm text-gray-400">Dati principali pronti per essere copiati</p>
            </div>
          </div>
          <button
            onClick={handleCopyRecap}
            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 text-sm ${
              copySuccess 
                ? 'bg-green-600 text-white' 
                : 'bg-blue-600 hover:bg-blue-500 text-white'
            }`}
          >
            {copySuccess ? (
              <>
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Copiato!</span>
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Copia Riepilogo</span>
              </>
            )}
          </button>
        </div>
        
        <div className="bg-gray-900/50 border border-gray-600 rounded-lg p-3 sm:p-4">
          <pre className="text-xs sm:text-sm text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
            {generateRecapText()}
          </pre>
        </div>
        
        <div className="mt-3 text-xs text-gray-500">
          💡 Clicca "Copia Riepilogo" per copiare tutto il testo negli appunti
        </div>
      </div>

      {/* Griglia Metriche Chiave */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Prezzo Apertura Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">${aggregatedMetrics.averageOpenPrice.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: ${aggregatedMetrics.medianOpenPrice.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-green-400/10 rounded-lg">
              <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Massimo Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">${aggregatedMetrics.averageHighPrice.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: ${aggregatedMetrics.medianHighPrice.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
              <Target className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Minimo Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">${aggregatedMetrics.averageLowPrice.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: ${aggregatedMetrics.medianLowPrice.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-yellow-400/10 rounded-lg">
              <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Chiusura Media</p>
              <p className="text-lg sm:text-xl font-bold text-white">${aggregatedMetrics.averageClosePrice.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: ${aggregatedMetrics.medianClosePrice.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-purple-400/10 rounded-lg">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Spike % Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{formatPercentage(aggregatedMetrics.averageSpikePercent)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {formatPercentage(aggregatedMetrics.medianSpikePercent)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Market Cap e Shares Outstanding */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-green-400/10 rounded-lg">
              <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Market Cap Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{formatMarketCap(aggregatedMetrics.averageMarketCap)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {formatMarketCap(aggregatedMetrics.medianMarketCap)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-yellow-400/10 rounded-lg">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Azioni in Circolazione Medie</p>
              <p className="text-lg sm:text-xl font-bold text-white">{formatVolume(aggregatedMetrics.averageSharesOutstanding)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {formatVolume(aggregatedMetrics.medianSharesOutstanding)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
              <PieChart className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Chiusura Rossa %</p>
              <p className="text-lg sm:text-xl font-bold text-white">{aggregatedMetrics.redClosePercentage.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
              <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Break PMH %</p>
              <p className="text-lg sm:text-xl font-bold text-white">{aggregatedMetrics.pmhBreakPercentage.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiche Performance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-yellow-400/10 rounded-lg">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Orario HOD Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{aggregatedMetrics.hodAverageTime}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
              <Timer className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Orario LOD Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{aggregatedMetrics.lodAverageTime}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-green-400/10 rounded-lg">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Spike % Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{formatPercentage(aggregatedMetrics.averageSpikePercent)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {formatPercentage(aggregatedMetrics.medianSpikePercent)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-yellow-400/10 rounded-lg">
              <BarChart2 className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Ratio Volume Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{aggregatedMetrics.averageVolumeRatio.toFixed(1)}x</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {aggregatedMetrics.medianVolumeRatio.toFixed(1)}x</p>
            </div>
          </div>
        </div>
      </div>

      {/* Metriche Performance Aggiuntive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
              <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">30 Min High % Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{formatPercentage(aggregatedMetrics.average30MinHighPercent)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {formatPercentage(aggregatedMetrics.median30MinHighPercent)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
              <Target className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">30 Min Low % Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{formatPercentage(aggregatedMetrics.average30MinLowPercent)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {formatPercentage(aggregatedMetrics.median30MinLowPercent)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-green-400/10 rounded-lg">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">1 Min High % Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{formatPercentage(aggregatedMetrics.average1MinHighPercent)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {formatPercentage(aggregatedMetrics.median1MinHighPercent)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
            <div className="p-1.5 sm:p-2 bg-red-400/10 rounded-lg">
              <Target className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">5 Min Low % Medio</p>
              <p className="text-lg sm:text-xl font-bold text-white">{formatPercentage(aggregatedMetrics.average5MinLowPercent)}</p>
              <p className="text-xs text-gray-500 mt-1">Mediana: {formatPercentage(aggregatedMetrics.median5MinLowPercent)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dati Volume e Pre-Market */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Analisi Volume Media</h3>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-400">Volume Pre-Market Medio</span>
              <div className="text-right">
                <span className="text-sm sm:text-base text-white font-medium">{formatVolume(aggregatedMetrics.averagePmVolume)}</span>
                <p className="text-xs text-gray-500">Mediana: {formatVolume(aggregatedMetrics.medianPmVolume)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-400">Volume EOD Medio</span>
              <div className="text-right">
                <span className="text-sm sm:text-base text-white font-medium">{formatVolume(aggregatedMetrics.averageEodVolume)}</span>
                <p className="text-xs text-gray-500">Mediana: {formatVolume(aggregatedMetrics.medianEodVolume)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-400">Ratio Volume Medio</span>
              <div className="text-right">
                <span className="text-sm sm:text-base text-white font-medium">{aggregatedMetrics.averageVolumeRatio.toFixed(2)}x</span>
                <p className="text-xs text-gray-500">Mediana: {aggregatedMetrics.medianVolumeRatio.toFixed(2)}x</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-400">Rotazione OS Media</span>
              <div className="text-right">
                <span className="text-sm sm:text-base text-white font-medium">{(aggregatedMetrics.averageOsRotation * 100).toFixed(2)}%</span>
                <p className="text-xs text-gray-500">Mediana: {(aggregatedMetrics.medianOsRotation * 100).toFixed(2)}%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Dati Pre-Market Medi</h3>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-400">Massimo PM Medio</span>
              <div className="text-right">
                <span className="text-sm sm:text-base text-white font-medium">${aggregatedMetrics.averagePmHigh.toFixed(2)}</span>
                <p className="text-xs text-gray-500">Mediana: ${aggregatedMetrics.medianPmHigh.toFixed(2)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-400">Minimo PM Medio</span>
              <div className="text-right">
                <span className="text-sm sm:text-base text-white font-medium">${aggregatedMetrics.averagePmLow.toFixed(2)}</span>
                <p className="text-xs text-gray-500">Mediana: ${aggregatedMetrics.medianPmLow.toFixed(2)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-400">Tasso Break PMH</span>
              <span className={`text-sm sm:text-base font-medium ${aggregatedMetrics.pmhBreakPercentage > 50 ? 'text-green-400' : 'text-red-400'}`}>
                {aggregatedMetrics.pmhBreakPercentage.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-400">PMH a High Medio</span>
              <div className="text-right">
                <span className="text-sm sm:text-base text-white font-medium">{formatPercentage(aggregatedMetrics.averagePmhToHighPercent)}</span>
                <p className="text-xs text-gray-500">Mediana: {formatPercentage(aggregatedMetrics.medianPmhToHighPercent)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Giorni Gap Individuali */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Giorni Gap Individuali ({aggregatedMetrics.totalOccurrences})</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Data</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Gap %</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Variazione %</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Market Cap</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Azioni in Circolazione</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Vol PM</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Vol EOD</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Break PMH</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Orario HOD</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Orario LOD</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wide">Stato</th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                  <span className="text-xs font-medium text-gray-300 uppercase tracking-wide">Spike %</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {aggregatedMetrics.allOccurrences.map((occurrence, index) => (
                <tr key={index} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-white">
                    {format(occurrence.Date, 'dd MMM yyyy', { locale: it })}
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3">
                    <span className={`text-xs sm:text-sm font-medium ${
                      occurrence['Gap %'] > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {formatPercentage(occurrence['Gap %'])}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3">
                    <span className={`text-xs sm:text-sm font-medium ${
                      occurrence['Change %'] > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {formatPercentage(occurrence['Change %'])}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-300">
                    {formatMarketCap(occurrence['Market Cap'])}
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-300">
                    {formatVolume(occurrence['Shares Outstanding'])}
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-300">
                    {formatVolume(occurrence['Pre Market Volume'])}
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-300">
                    {formatVolume(occurrence.Volume)}
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3">
                    <span className={`text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ${
                      occurrence['HIGH > PMH'] === 'Y' 
                        ? 'bg-green-400/10 text-green-400' 
                        : 'bg-red-400/10 text-red-400'
                    }`}>
                      {occurrence['HIGH > PMH'] === 'Y' ? 'SÌ' : 'NO'}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-300">
                    {occurrence['Hod Time'] instanceof Date 
                      ? `${occurrence['Hod Time'].getHours().toString().padStart(2, '0')}:${occurrence['Hod Time'].getMinutes().toString().padStart(2, '0')}`
                      : '--:--'
                    }
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-300">
                    {occurrence['Lod Time'] instanceof Date 
                      ? `${occurrence['Lod Time'].getHours().toString().padStart(2, '0')}:${occurrence['Lod Time'].getMinutes().toString().padStart(2, '0')}`
                      : '--:--'
                    }
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3">
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        occurrence['Change %'] < 0 ? 'bg-red-400' : 'bg-green-400'
                      }`} />
                      <span className="text-xs text-gray-400">
                        {occurrence['Change %'] < 0 ? 'ROSSO' : 'VERDE'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <span className={`text-xs sm:text-sm font-medium ${
                      occurrence['Spike %'] > 0 ? 'text-yellow-400' : 'text-gray-400'
                    }`}>
                      {formatPercentage(occurrence['Spike %'])}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};